import { get } from 'lodash';
import * as React from 'react';
import { Col, Row } from 'react-flexbox-grid';
import { FormSection } from 'redux-form';

import {
  AutoComplete,
  AutoCompleteField,
  Date,
  DateField,
  FormattedNumberInput,
  Input,
  InputField,
  RemoteAutoComplete,
  RemoteAutoCompleteField,
  RemoteAutoCompleteTree,
  RemoteAutoCompleteTreeField,
  Toggle,
  ToggleField
} from '@components/form';

import { educationTypeEnum, fuelTypeEnum } from '@modules/dictionary/_dictionaryEnums';

import AddressComponent, { AddressField } from '@components/form/Address/Address';
import { floatOnly, formatToDecimals } from '@utils/utils';
import { isPositive, required } from '@utils/validators';

const fieldRowInput = (
  label,
  name,
  type = 'text',
  multiline = false,
  labelSize = 2,
  inputSize = 12 - labelSize,
  validate = [] as Function[]
) => (
  <Row>
    <Col md={labelSize}>
      <div style={{ marginTop: 10 }}>{label}</div>
    </Col>
    <Col md={inputSize}>
      <InputField component={Input} name={name} type={type} multiLine={multiline} validate={validate} />
    </Col>
  </Row>
);
const fieldRowNumber = (label, name, validate = [] as Function[]) => (
  <Row>
    <Col md={6}>
      <div style={{ marginTop: 10 }}>{label}</div>
    </Col>
    <Col md={6}>
      <InputField
        component={FormattedNumberInput}
        name={name}
        id={name}
        normalize={floatOnly}
        format={v => formatToDecimals(v)}
        validate={validate}
      />
    </Col>
  </Row>
);
const fieldRowDate = (label, name, dateOnly = false, allowFuture = false) => (
  <Row>
    <Col md={6}>
      <div style={{ marginTop: 10 }}>{label}</div>
    </Col>
    <Col md={6}>
      <DateField component={Date} name={name} id={name} dateOnly={dateOnly} allowFuture={allowFuture} />
    </Col>
  </Row>
);
const fieldRowToggle = (change, label, name, labelSize = 2, inputSize = 12 - labelSize) => (
  <Row>
    <Col md={labelSize}>
      <div style={{ marginTop: 10 }}>{label}</div>
    </Col>
    <Col md={inputSize}>
      <div style={{ marginTop: 10 }}>
        <ToggleField
          component={Toggle}
          name={name}
          labelPosition="right"
          onToggle={(_, value) => change(name, value)}
        />
      </div>
    </Col>
  </Row>
);
const fieldRowRemote = (
  label,
  name,
  url,
  labelProp = 'name',
  valueFunction = o => o,
  labelSize = 2,
  inputSize = 12 - labelSize
) => (
  <Row>
    <Col md={labelSize}>
      <div style={{ marginTop: 10 }}>{label}</div>
    </Col>
    <Col md={inputSize}>
      <RemoteAutoCompleteField
        component={RemoteAutoComplete}
        id={name}
        name={name}
        url={`/metropolis/api/${url}?pageSize=9999&filter%5B0%5D.field=active&filter%5B0%5D.operation=EQ&filter%5B0%5D.value=true`}
        fullWidth={true}
        labelFunction={o => get(o, labelProp)}
        valueFunction={valueFunction}
        arrowstyle={{
          color: '#e1e0e1',
          position: 'absolute',
          right: 0,
          top: 10
        }}
      />
    </Col>
  </Row>
);
const fieldRowAutoComplete = (label, name, items, labelSize = 4, inputSize = 12 - labelSize) => (
  <Row>
    <Col md={labelSize}>
      <div style={{ marginTop: 10 }}>{label}</div>
    </Col>
    <Col md={inputSize}>
      <AutoCompleteField
        component={AutoComplete}
        id={name}
        name={name}
        items={items}
        fullWidth={true}
        labelFunction={o => get(o, 'label')}
        valueFunction={o => get(o, 'id')}
      />
    </Col>
  </Row>
);

export const EventType = ({ change }) => (
  <Row>
    <Col md={4}>
      <div style={{ marginTop: 10 }}>Nadřazený typ</div>
    </Col>
    <Col md={8}>
      <RemoteAutoCompleteTreeField
        component={RemoteAutoCompleteTree}
        id={'parent'}
        change={change}
        formName={'DictionaryGrid-event-types'}
        name={'parent'}
        itemLabel={'name'}
        loopControl={true}
        url={`/metropolis/api/ed/types`}
        fullWidth={true}
        labelFunction={o => get(o, 'name')}
        valueFunction={o => ({
          id: get(o, 'id')
        })}
      />
    </Col>
  </Row>
);

export const DelictType = ({ change, context }) => {
  const withTreeTypes = get(context, 'config.extensions.delict-types-tree') === 'true';

  return withTreeTypes ? (
    <Row>
      <Col md={4}>
        <div style={{ marginTop: 10 }}>Nadřazený typ</div>
      </Col>
      <Col md={8}>
        <RemoteAutoCompleteTreeField
          component={RemoteAutoCompleteTree}
          id={'parent'}
          change={change}
          formName={'DictionaryGrid-delict-types'}
          name={'parent'}
          itemLabel={'name'}
          loopControl={true}
          url={'/metropolis/api/ed/delict/types'}
          fullWidth={true}
          labelFunction={o => get(o, 'fullName')}
          valueFunction={o => ({
            id: get(o, 'id')
          })}
        />
      </Col>
    </Row>
  ) : (
    <div />
  );
};

// Toggles
export const Active = ({ change }) => fieldRowToggle(change, 'Aktivní', 'active');
export const ActiveWide = ({ change }) => fieldRowToggle(change, 'Aktivní', 'active', 4);

// Input fields
export const Order = () => fieldRowInput('Pořadí', 'order', 'number', undefined, 2, 10, [isPositive]);
export const OrderWide = () => fieldRowInput('Pořadí', 'order', 'number', undefined, 4, 8, [isPositive]);
export const Name = () => fieldRowInput('Název', 'name');
export const NameWide = () => fieldRowInput('Název', 'name', undefined, undefined, 4);
export const Code = () => fieldRowInput('Kód', 'code', 'number', undefined, 2, 10, [isPositive]);
export const ShreddingSymbol = () => fieldRowInput('Skartační znak', 'shreddingSymbol');
export const ShreddingPeriod = () => fieldRowInput('Skartační lhůta', 'shreddingPeriod');
export const Zip = () => fieldRowInput('PSČ', 'zip', 'number');
export const Template = () => fieldRowInput('Šablona', 'template', 'text', true);
export const Reminder = () => fieldRowInput('Upozornění', 'reminder', 'number');
export const Law = () => fieldRowInput('Zákon', 'law');
export const Amendment = () => fieldRowInput('Novela', 'amendment');
export const Paragraph = () => fieldRowInput('Paragraf', 'paragraph');
export const Section = () => fieldRowInput('Odstavec', 'section', 'number');
export const Letter = () => fieldRowInput('Písmeno', 'letter');
export const PointOfLetter = () => fieldRowInput('Bod', 'pointOfLetter');
export const Points = () => fieldRowInput('Body', 'points');
export const Description = () => fieldRowInput('Popis', 'description', 'text', true);
export const IdNumber = () => fieldRowInput('IČ', 'idNumber');
export const Contact = () => fieldRowInput('Kontakt', 'contact');
export const PhoneNumber = () => fieldRowInput('Telefon', 'phoneNumber');
export const Email = () => fieldRowInput('Email', 'email');
export const Format = () => fieldRowInput('Formát', 'format');
export const Counter = () => fieldRowInput('Hodnota', 'counter');
export const Mileage = () => fieldRowInput('Stav km', 'mileage', 'number', undefined, 4, undefined, [required]);
export const FuelCosts = () => fieldRowNumber('Celkové náklady na palivo', 'fuelCosts', [required]);
export const RepairCosts = () => fieldRowNumber('Celkové náklady na opravy', 'repairCosts');
export const FuelConsumption = () =>
  fieldRowInput('Spotřeba na 100 km', 'fuelConsumption', 'number', undefined, 8, undefined, [required]);
export const OilType = () => fieldRowInput('Typ oleje', 'oilType', undefined, undefined, 5);
export const TireSize = () => fieldRowInput('Typ Pneumatik', 'tireSize', undefined, undefined, 5);
export const LastInspection = () =>
  fieldRowInput('Nájezd při poslední prohlídce', 'lastInspection', 'number', undefined, 6);
export const nextInspection = () =>
  fieldRowInput('Nájezd při příští prohlídce', 'nextInspection', 'number', undefined, 6);
export const LastGeneralInspection = () =>
  fieldRowInput('Nájezd při poslední generální prohlídce', 'lastGeneralInspection', 'number', undefined, 6);
export const GeneralInspectionMileage = () =>
  fieldRowInput('Nájezd kdy notifikovat o generální prohlídce', 'generalInspectionMileage', 'number', undefined, 6);
export const StationCode = () => fieldRowInput('Kód', 'code');

export const ViolationLaw = () => fieldRowInput('Zákon porušení', 'violationLaw');
export const DelictLaw = () => fieldRowInput('Zákon přestupku', 'delictLaw');

export const Symbol = () => fieldRowInput('Symbol', 'symbol');

// Date fields
export const NextStkTest = () => fieldRowDate('Datum příští STK', 'nextStkTest', false, true);
export const GeneralInspectionDate = () =>
  fieldRowDate('Datum kdy notifikovat o generální prohlídce', 'generalInspectionDate', false, true);

// Autocompletes fields
export const FuelType = () => fieldRowAutoComplete('Typ paliva', 'fuelType', fuelTypeEnum);
export const EducationType = () => fieldRowAutoComplete('Typ vzdělání', 'type', educationTypeEnum, 2, 10);

// Remote autcompletes
export const Country = () => fieldRowRemote('Stát', 'country', 'common/countries');
export const Region = () => fieldRowRemote('Kraj', 'region', 'common/regions');
export const County = () => fieldRowRemote('Okres', 'county', 'common/counties');
export const Municipality = () => fieldRowRemote('Obec', 'municipality', 'common/municipalities');
export const Street = () => fieldRowRemote('Ulice', 'street', 'common/streets');
export const Report = () => fieldRowRemote('Report', 'descriptionTemplate', 'reports', undefined, undefined, 4);
export const Type = () => fieldRowRemote('Typ vozidla', 'type', 'wv/types', undefined, o => get(o, 'name'), 5);
export const Brand = () => fieldRowRemote('Značka vozidla', 'brand', 'wv/brands', undefined, o => get(o, 'name'), 5);
export const Color = () => fieldRowRemote('Barva vozidla', 'color', 'wv/colors', undefined, o => get(o, 'name'), 5);
export const CallSign = () =>
  fieldRowRemote('Volací znak', 'callSign', 'cl/callsign', undefined, undefined, 4, undefined);

// Compound field
export const StreetNumbers = () => (
  <Row>
    <Col md={2}>
      <div style={{ marginTop: 10 }}>Č. p.</div>
    </Col>
    <Col md={4}>
      <InputField component={Input} name="descriptiveNumber" />
    </Col>
    <Col md={2}>
      <div style={{ marginTop: 10 }}>Č. o.</div>
    </Col>
    <Col md={4}>
      <InputField component={Input} name="orientationNumber" />
    </Col>
  </Row>
);

export const VehicleIds = () => (
  <Col md={12}>
    <Row>
      <Col md={8}>
        <div style={{ marginTop: 10 }}>{'Registrační číslo vozdila *'}</div>
      </Col>
      <Col md={4}>
        <InputField component={Input} name={'registrationNumber'} validate={required} />
      </Col>
    </Row>
    <Row>
      <Col md={8}>
        <div style={{ marginTop: 10 }}>{'VIN'}</div>
      </Col>
      <Col md={4}>
        <InputField component={Input} name={'vinNumber'} />
      </Col>
    </Row>
  </Col>
);

// Address
export const Address = () => <AddressField component={AddressComponent} name={'address'} />;

// ServiceVehicleVehicle
export const ServiceVehicleVehicle = () => (
  <FormSection name="vehicle">
    <Row>
      <Col md={6} className="with-right-divider">
        <h3 style={{ marginTop: '15px' }}>{'Čísla'}</h3>
        <VehicleIds />
      </Col>
      <Col md={6}>
        <h3 style={{ marginTop: '15px' }}>{'Typy'}</h3>
        <Brand />
        <Type />
        <Color />
      </Col>
    </Row>
  </FormSection>
);

export default [Order, Name, Active];



// WEBPACK FOOTER //
// ./src/modules/dictionary/DictionaryFields.tsx